import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRightIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import LandingNavbar from "../components/landing/LandingNavbar";
import Footer from "../components/landing/Footer";
import { marketingOrigin } from "../data/seo/siteEntity";
import { CASE_STUDIES } from "../data/caseStudies";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import { buildCollectionPageSchema } from "../data/seo/structuredData";

const SEO_TITLE = "Customer Case Studies — FloGPT";
const SEO_DESCRIPTION =
    "Real FloGPT customer deployments. See how OutreachBox, Pulse Real, and InclusiveSpeak ship AI chat, voice agents, and unified CRM on production websites.";
const SEO_KEYWORDS = [
    "FloGPT case studies",
    "AI chat case study",
    "AI voice agent case study",
    "FloGPT customers",
    "OutreachBox",
    "Pulse Real",
    "InclusiveSpeak",
];

export default function CaseStudiesIndex() {
    usePageSeo({
        title: SEO_TITLE,
        description: SEO_DESCRIPTION,
        keywords: SEO_KEYWORDS.join(", "),
        canonical: "/case-studies",
    });

    const collectionSchema = useMemo(
        () =>
            buildCollectionPageSchema({
                name: "FloGPT customer case studies",
                description: SEO_DESCRIPTION,
                path: "/case-studies",
                itemCount: CASE_STUDIES.length,
            }),
        [],
    );

    const breadcrumbSchema = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${marketingOrigin()}/` },
                { "@type": "ListItem", position: 2, name: "Case Studies", item: `${marketingOrigin()}/case-studies` },
            ],
        }),
        [],
    );

    const itemListSchema = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "FloGPT customer case studies",
            numberOfItems: CASE_STUDIES.length,
            itemListElement: CASE_STUDIES.map((study, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                url: `${marketingOrigin()}/case-studies/${study.slug}`,
                name: `${study.brand} — ${study.useCase}`,
            })),
        }),
        [],
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    return (
        <div className="bg-slate-900 min-h-screen overflow-x-hidden text-slate-100">
            <LandingNavbar />

            <main className="pt-20">
                <section className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 -z-10">
                        <div className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-orange-500/15 blur-3xl" />
                        <div className="absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-violet-500/15 blur-3xl" />
                        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
                    </div>

                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="max-w-3xl"
                        >
                            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                Case studies · Live deployments
                            </div>
                            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
                                Teams shipping FloGPT in{" "}
                                <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                                    production today
                                </span>
                            </h1>
                            <p className="mt-6 text-lg leading-relaxed text-slate-300">
                                Short, concrete stories: the problem, the FloGPT surfaces deployed, and what
                                changed. Every customer below is reachable on the live web — click through to
                                see the agent in action.
                            </p>
                        </motion.div>
                    </div>
                </section>

                <section className="relative pb-20">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {CASE_STUDIES.map((study, idx) => {
                                const Icon = study.Icon;
                                return (
                                    <motion.div
                                        key={study.slug}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: Math.min(idx * 0.06, 0.2) }}
                                    >
                                        <Link
                                            to={`/case-studies/${study.slug}`}
                                            className="group relative block h-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:border-white/20 hover:bg-white/10 transition-all overflow-hidden"
                                        >
                                            <div
                                                className={`pointer-events-none absolute -inset-4 rounded-3xl blur-2xl opacity-0 group-hover:opacity-60 transition-opacity ${study.softAccent}`}
                                            />
                                            <div className="relative">
                                                <div className="flex items-start justify-between mb-4">
                                                    <div
                                                        className={`h-12 w-12 rounded-xl bg-gradient-to-br ${study.accent} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}
                                                    >
                                                        <Icon className="h-6 w-6 text-white" />
                                                    </div>
                                                    <span
                                                        className={`text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r ${study.accent} bg-clip-text text-transparent text-right`}
                                                    >
                                                        {study.useCase}
                                                    </span>
                                                </div>

                                                <h3 className="text-xl font-bold text-white group-hover:text-orange-400 transition-colors">
                                                    {study.brand}
                                                </h3>
                                                <div className="mt-1 text-[11px] font-mono text-slate-500">{study.domain}</div>
                                                <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-4">
                                                    {study.tagline}
                                                </p>

                                                <div className="mt-5 flex flex-wrap gap-3">
                                                    {study.results.slice(0, 2).map((metric) => (
                                                        <div
                                                            key={metric.label}
                                                            className="rounded-lg border border-white/10 bg-slate-900/60 px-3 py-1.5"
                                                        >
                                                            <div className="text-sm font-bold text-white">{metric.value}</div>
                                                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                                {metric.label}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400">
                                                    Read the case study
                                                    <ArrowRightIcon className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section className="relative pb-24">
                    <div className="mx-auto max-w-5xl px-6 lg:px-8">
                        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-10 sm:p-14 text-center overflow-hidden">
                            <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />
                            <div className="relative">
                                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                    Want to be the next case study?
                                </h2>
                                <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl mx-auto">
                                    Spin up a free FloGPT workspace, ship an AI chat or voice agent on your own
                                    site this week, and we'll write up the deployment with you.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                                    >
                                        Start free
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </Link>
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Talk to a human
                                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />

            <JsonLd id="case-studies-collection" data={collectionSchema} />
            <JsonLd id="case-studies-breadcrumb" data={breadcrumbSchema} />
            <JsonLd id="case-studies-itemlist" data={itemListSchema} />
        </div>
    );
}
